/**
 * Auto-variation runner. When Auto is turned on in the True/Magnetic control, this
 * keeps the magnetic variation in step with the World Magnetic Model at the boat's
 * position, so bearings shown in °M use the right variation for where we actually
 * are, with nothing to type in. It's the same idea as the auto palette that
 * follows the sun, just feeding the variation instead of the day/night tint.
 *
 * How it decides: the model (nav/wmm.ts) gives the declination for ownship
 * lat/long and today's date. Declination barely shifts day to day, but it does
 * change across the ground, so we recompute on a slow timer for the date and
 * promptly whenever the boat moves enough to matter.
 *
 * No fix yet: we hand the store a null so it falls back to the hand-entered manual
 * variation rather than guessing a value out of thin air. Renders nothing; mirrors
 * AutoPaletteRunner.
 */
import { useEffect, useRef } from 'react';
import { useChartPlotterStore } from '@/store';
import { useBearingDisplayStore } from '@/store/bearingDisplay';
import { magneticDeclinationDeg } from '@/nav';

/** How often to re-check the model (ms). Six hours is ample — variation creeps. */
const RECHECK_MS = 6 * 60 * 60 * 1000;
/** Only recompute on a position move this large (deg) — ~6 nm. */
const MOVE_DEG = 0.1;

export function AutoVariationRunner() {
  const auto = useBearingDisplayStore((s) => s.auto);
  // Track the last position we computed for, so a jittering fix doesn't recompute
  // every tick — only a real move does.
  const lastPos = useRef<[number, number] | null>(null);

  useEffect(() => {
    if (!auto) {
      lastPos.current = null;
      return;
    }
    const apply = (): void => {
      const pos = useChartPlotterStore.getState().ownship.position;
      const set = useBearingDisplayStore.getState().setAutoComputedVariation;
      if (!pos) {
        // No fix — clear the computed figure so the manual value stands in.
        set(null);
        lastPos.current = null;
        return;
      }
      const [lon, lat] = pos;
      set(magneticDeclinationDeg(lat, lon));
      lastPos.current = pos;
    };

    apply();
    const timer = setInterval(apply, RECHECK_MS);
    // Recompute promptly on a meaningful position change (or the first fix), not
    // just on the slow timer, so moving into a new area updates the variation.
    const unsub = useChartPlotterStore.subscribe((s) => {
      const pos = s.ownship.position;
      if (!pos) return;
      const last = lastPos.current;
      if (!last || Math.abs(pos[0] - last[0]) >= MOVE_DEG || Math.abs(pos[1] - last[1]) >= MOVE_DEG) {
        apply();
      }
    });

    return () => {
      clearInterval(timer);
      unsub();
    };
  }, [auto]);

  return null;
}
